import { useState, useEffect } from "react";
import { Search, ChevronUp, Info, Gamepad2, Trophy, Printer, Keyboard, Smartphone, Flame, ArrowLeft } from "lucide-react";
import SopSwitcher from "./SopSwitcher";
import SopImage from "./SopImage";
import "../styles/tutorialPrint.css";

const SECTIONS = [
  { id: "arcade-overview", num: "1", title: "RRDC Sports Arcade — Overview" },
  { id: "arcade-setup", num: "2", title: "Getting Started — Set Your Name" },
  { id: "arcade-baseball", num: "3", title: "Baseball" },
  { id: "arcade-football", num: "4", title: "Football" },
  { id: "arcade-bowling", num: "5", title: "Bowling" },
  { id: "arcade-basketball", num: "6", title: "Basketball" },
  { id: "arcade-hockey", num: "7", title: "Hockey" },
  { id: "arcade-soccer", num: "8", title: "Soccer" },
  { id: "arcade-leaderboard", num: "9", title: "Leaderboard" },
  { id: "lava-surfer", num: "10", title: "Lava Surfer — Overview" },
  { id: "lava-controls", num: "11", title: "Lava Surfer — Controls & How to Play" },
  { id: "lava-leaderboard", num: "12", title: "Lava Surfer — Leaderboard" },
];

function H({ id, num, title, icon: Icon }) {
  return (
    <div id={id} className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-6 h-6 text-violet-600 flex-shrink-0" />}
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{num}. {title}</h2>
      </div>
    </div>
  );
}

export default function TutorialGames() {
  const [search, setSearch] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const filtered = search ? SECTIONS.filter(s => s.title.toLowerCase().includes(search.toLowerCase())) : SECTIONS;

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="tutorial-games-page">
      <header className="sticky top-0 z-50 bg-violet-900 text-white shadow-lg print:static print:bg-white print:text-black">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Rolling River Day Camp</h1>
              <p className="text-xs text-violet-200">Games — How to Play</p>
            </div>
            <div className="hidden sm:block">
              <SopSwitcher current="/tutorialgames" accent="purple" />
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <div className="sm:hidden">
              <SopSwitcher current="/tutorialgames" accent="purple" />
            </div>
            <button onClick={() => window.print()} className="text-xs bg-violet-700 hover:bg-violet-600 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1" data-testid="print-btn">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <a href="/help" className="text-xs bg-violet-700 hover:bg-violet-600 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1" data-testid="back-to-help">
              <ArrowLeft className="w-3.5 h-3.5" /> All Guides
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 lg:flex lg:gap-8">
        <nav aria-label="TOC" className="lg:w-64 lg:flex-shrink-0 mb-6 lg:mb-0 print:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden w-full text-left bg-gray-800 border border-gray-700 rounded-xl p-3 text-sm font-bold text-gray-200 flex items-center justify-between" data-testid="toc-toggle">
            Table of Contents <ChevronUp className={`w-4 h-4 transition-transform ${menuOpen ? "" : "rotate-180"}`} />
          </button>
          <div className={`lg:sticky lg:top-20 ${menuOpen ? "block" : "hidden lg:block"} mt-2 lg:mt-0`}>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-500" />
              <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-700 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400" data-testid="toc-search" />
            </div>
            <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {filtered.map(s => (
                <li key={s.id}><a href={`#${s.id}`} onClick={() => setMenuOpen(false)} className="block px-3 py-1.5 text-sm rounded-lg hover:bg-violet-900/50 text-gray-400 hover:text-white">{s.num}. {s.title}</a></li>
              ))}
            </ul>
          </div>
        </nav>

        <main className="flex-1 min-w-0">
          {/* Intro */}
          <section className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Camp Games</h2>
            <p className="text-base text-gray-300 leading-relaxed mb-4">Rolling River has two game sites campers and staff can play. Here's how each one works.</p>
            <div className="grid grid-cols-2 gap-3">
              <a href="https://rrdcarcade.com" target="_blank" rel="noopener noreferrer" className="p-4 bg-gradient-to-br from-green-900 to-green-800 rounded-2xl border border-green-600 hover:border-green-400 transition-colors text-center" data-testid="link-arcade">
                <Gamepad2 className="w-8 h-8 mx-auto text-green-300 mb-2" />
                <p className="font-bold text-green-100">RRDC Sports Arcade</p>
                <p className="text-xs text-green-300 mt-1">rrdcarcade.com</p>
                <p className="text-[10px] text-green-400 mt-0.5">6 sports games + leaderboard</p>
              </a>
              <a href="https://lava-surfer.com" target="_blank" rel="noopener noreferrer" className="p-4 bg-gradient-to-br from-orange-900 to-red-900 rounded-2xl border border-orange-600 hover:border-orange-400 transition-colors text-center" data-testid="link-lava">
                <Flame className="w-8 h-8 mx-auto text-orange-300 mb-2" />
                <p className="font-bold text-orange-100">Lava Surfer</p>
                <p className="text-xs text-orange-300 mt-1">lava-surfer.com</p>
                <p className="text-[10px] text-orange-400 mt-0.5">Endless runner — ride the lava!</p>
              </a>
            </div>
          </section>

          {/* ═══ RRDC SPORTS ARCADE ═══ */}
          <H id="arcade-overview" num="1" title="RRDC Sports Arcade — Overview" icon={Gamepad2} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-300"><strong className="text-white">rrdcarcade.com</strong> — The Rolling River Sports Arcade. 6 retro-style arcade sports games featuring Sami the Shark, the camp mascot. Play on any phone, tablet, or computer. No login needed — just pick a game and play.</p>
            <SopImage filename="arcade_home.png" caption="rrdcarcade.com home — pick a sport to play. Settings gear (top-left) lets you set your leaderboard name." />
            <p className="text-base text-gray-300 font-bold mt-3">The 6 games:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { name: "Baseball", color: "bg-green-600", desc: "Swing timing" },
                { name: "Football", color: "bg-orange-600", desc: "Throw accuracy" },
                { name: "Bowling", color: "bg-violet-600", desc: "Aim & power" },
                { name: "Basketball", color: "bg-orange-500", desc: "Shot angle" },
                { name: "Hockey", color: "bg-cyan-600", desc: "Slap shot" },
                { name: "Soccer", color: "bg-green-500", desc: "Goal kicks" },
              ].map(g => (
                <div key={g.name} className={`p-3 ${g.color} rounded-xl text-center`}>
                  <p className="font-bold text-white text-sm">{g.name}</p>
                  <p className="text-[10px] text-white/70">{g.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ═══ 2. SETUP ═══ */}
          <H id="arcade-setup" num="2" title="Getting Started — Set Your Name" icon={Gamepad2} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-300">Before playing, set your name so it shows on the leaderboard:</p>
            <ol className="list-decimal pl-6 space-y-2 text-base text-gray-300">
              <li>Go to <strong className="text-white">rrdcarcade.com</strong>.</li>
              <li>Tap the <strong className="text-white">gear icon</strong> in the top-left corner.</li>
              <li>Type your name (first name is fine). This is what shows on the leaderboard.</li>
              <li>Close the settings. Pick any game from the menu.</li>
            </ol>
            <div className="p-3 bg-violet-900/50 border border-violet-600 rounded-xl">
              <p className="text-sm text-violet-200">Works on phones, tablets, and computers. All games use touch/click controls — no keyboard required (but keyboard works too).</p>
            </div>
          </div>

          {/* ═══ 3-8. INDIVIDUAL GAMES ═══ */}
          <H id="arcade-baseball" num="3" title="Baseball" icon={Gamepad2} />
          <div className="mt-4 space-y-3">
            <div className="p-4 bg-green-900/30 border border-green-700 rounded-xl">
              <p className="font-bold text-green-300 mb-2">How to Play</p>
              <p className="text-sm text-gray-300">You are the batter. A pitch is thrown toward you. Tap or click at the right moment to swing.</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-300">
                <li><strong className="text-white">Timing is everything</strong> — swing too early or too late and you miss or foul.</li>
                <li><strong className="text-white">Perfect timing</strong> = home run. Good timing = base hit. Bad timing = strike.</li>
                <li><strong className="text-white">3 strikes</strong> and you're out. Try to hit as many home runs as possible before you strike out.</li>
                <li><strong className="text-white">Score</strong> = runs batted in. More home runs = higher score.</li>
              </ul>
              <p className="text-xs text-green-400 mt-2">Controls: Tap screen / Click / Space bar to swing</p>
            </div>
          </div>

          <H id="arcade-football" num="4" title="Football" icon={Gamepad2} />
          <div className="mt-4 space-y-3">
            <div className="p-4 bg-orange-900/30 border border-orange-700 rounded-xl">
              <p className="font-bold text-orange-300 mb-2">How to Play</p>
              <p className="text-sm text-gray-300">You are the quarterback. Your receiver runs a route downfield. Throw the ball to hit the target.</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-300">
                <li><strong className="text-white">Aim</strong> — tap/click where you want the ball to land. Lead the receiver.</li>
                <li><strong className="text-white">Accuracy matters</strong> — hitting the receiver in stride = touchdown. Missing = incomplete.</li>
                <li><strong className="text-white">Defenders</strong> try to intercept. Throw past them.</li>
                <li><strong className="text-white">Score</strong> = touchdowns scored. More TDs = higher score.</li>
              </ul>
              <p className="text-xs text-orange-400 mt-2">Controls: Tap/click to aim and throw</p>
            </div>
          </div>

          <H id="arcade-bowling" num="5" title="Bowling" icon={Gamepad2} />
          <div className="mt-4 space-y-3">
            <div className="p-4 bg-violet-900/30 border border-violet-700 rounded-xl">
              <p className="font-bold text-violet-300 mb-2">How to Play</p>
              <p className="text-sm text-gray-300">Classic bowling. Aim your ball, set the power, and release to knock down pins.</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-300">
                <li><strong className="text-white">Aim</strong> — an arrow moves left and right. Tap once to set your direction.</li>
                <li><strong className="text-white">Power</strong> — a power meter fills up. Tap again to set how hard you throw.</li>
                <li><strong className="text-white">Release</strong> — the ball rolls down the lane. Knock down all 10 pins for a strike.</li>
                <li><strong className="text-white">10 frames</strong> per game, just like real bowling. Max score is 300 (perfect game).</li>
              </ul>
              <p className="text-xs text-violet-400 mt-2">Controls: Tap/click to aim, tap again for power, auto-release</p>
            </div>
          </div>

          <H id="arcade-basketball" num="6" title="Basketball" icon={Gamepad2} />
          <div className="mt-4 space-y-3">
            <div className="p-4 bg-orange-900/30 border border-orange-600 rounded-xl">
              <p className="font-bold text-orange-300 mb-2">How to Play</p>
              <p className="text-sm text-gray-300">Shoot hoops. Set your angle and power to sink baskets from different positions on the court.</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-300">
                <li><strong className="text-white">Angle</strong> — aim the arc of your shot. Higher arc = more forgiving but slower.</li>
                <li><strong className="text-white">Power</strong> — set how hard you throw. Too much and you overshoot. Too little and you airball.</li>
                <li><strong className="text-white">Swish</strong> = nothing but net (bonus points). Rim shot = still counts but fewer points.</li>
                <li><strong className="text-white">The hoop moves</strong> at higher levels. Stay accurate.</li>
              </ul>
              <p className="text-xs text-orange-400 mt-2">Controls: Tap/click and drag to set angle + power, release to shoot</p>
            </div>
          </div>

          <H id="arcade-hockey" num="7" title="Hockey" icon={Gamepad2} />
          <div className="mt-4 space-y-3">
            <div className="p-4 bg-cyan-900/30 border border-cyan-700 rounded-xl">
              <p className="font-bold text-cyan-300 mb-2">How to Play</p>
              <p className="text-sm text-gray-300">Take slap shots at the goal. The goalie moves to block. Find the opening and shoot.</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-300">
                <li><strong className="text-white">Aim</strong> — move your aim cursor to target a corner of the net.</li>
                <li><strong className="text-white">Shoot</strong> — tap/click to take the shot. The goalie will try to save it.</li>
                <li><strong className="text-white">Top corners</strong> are harder to hit but harder for the goalie to reach.</li>
                <li><strong className="text-white">Score</strong> = goals scored in the time limit.</li>
              </ul>
              <p className="text-xs text-cyan-400 mt-2">Controls: Move cursor to aim, tap/click to shoot</p>
            </div>
          </div>

          <H id="arcade-soccer" num="8" title="Soccer" icon={Gamepad2} />
          <div className="mt-4 space-y-3">
            <div className="p-4 bg-green-900/30 border border-green-600 rounded-xl">
              <p className="font-bold text-green-300 mb-2">How to Play</p>
              <p className="text-sm text-gray-300">Take penalty kicks. Aim past the goalkeeper and put the ball in the back of the net.</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-300">
                <li><strong className="text-white">Aim</strong> — choose where in the goal you want to kick. Left, right, high, low.</li>
                <li><strong className="text-white">Power</strong> — set your kick power. Too much and it goes over the bar.</li>
                <li><strong className="text-white">The keeper dives</strong> — predict which way they'll go and shoot the other way.</li>
                <li><strong className="text-white">Score</strong> = goals scored out of your attempts.</li>
              </ul>
              <p className="text-xs text-green-400 mt-2">Controls: Tap/click to aim and kick</p>
            </div>
          </div>

          {/* ═══ 9. LEADERBOARD ═══ */}
          <H id="arcade-leaderboard" num="9" title="Leaderboard" icon={Trophy} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-300">Every game tracks high scores. Tap the <strong className="text-yellow-400">LEADERBOARD</strong> button on the home screen to see the all-time top scores across every sport.</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-300">
              <li>Shows player name + score for each game</li>
              <li>Updated in real time after every game</li>
              <li>Your name comes from the settings gear (Section 2)</li>
              <li>Beat the top score to take #1</li>
            </ul>
          </div>

          {/* ═══ LAVA SURFER ═══ */}
          <H id="lava-surfer" num="10" title="Lava Surfer — Overview" icon={Flame} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-300"><strong className="text-orange-400">lava-surfer.com</strong> — "Ride the flames, dodge the rocks!" A retro pixel-art endless runner starring Sami the Shark. Surf through a lava river, dodge obstacles, and survive as long as you can. The longer you survive, the higher your score.</p>
            <SopImage filename="lavasurfer_home.png" caption="lava-surfer.com title screen — tap Start Game to play." />
            <div className="p-3 bg-orange-900/30 border border-orange-600 rounded-xl">
              <p className="text-sm text-orange-200">Made by Coley. Works on phones, tablets, and computers. No login needed — just tap Start Game.</p>
            </div>
          </div>

          <H id="lava-controls" num="11" title="Lava Surfer — Controls & How to Play" icon={Keyboard} />
          <div className="mt-4 space-y-3">
            <div className="p-4 bg-red-900/30 border border-red-700 rounded-xl">
              <p className="font-bold text-red-300 mb-3">Controls</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Keyboard className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-white">Keyboard (Computer)</p>
                    <p className="text-xs text-gray-300">Arrow Keys or WASD = Move left/right/up/down</p>
                    <p className="text-xs text-gray-300">Space or Up Arrow = Jump</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-white">Touch (Phone / Tablet)</p>
                    <p className="text-xs text-gray-300">Tap left side = move left, Tap right side = move right</p>
                    <p className="text-xs text-gray-300">Swipe up = Jump</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-base text-gray-300 font-bold mt-3">How to play:</p>
            <ol className="list-decimal pl-6 space-y-2 text-base text-gray-300">
              <li>Go to <strong className="text-white">lava-surfer.com</strong>.</li>
              <li>Tap <strong className="text-orange-400">START GAME</strong> or press <strong className="text-white">Space</strong>.</li>
              <li>Sami the Shark starts surfing through a lava river. <strong className="text-white">Dodge the rocks</strong> coming toward you.</li>
              <li>Move left and right to avoid obstacles. Jump over low rocks.</li>
              <li>The longer you survive, the faster it gets. Your score goes up every second you stay alive.</li>
              <li>When you hit a rock, game over. Enter your name if you made the top 10.</li>
            </ol>

            <div className="p-3 bg-yellow-900/30 border border-yellow-600 rounded-xl">
              <p className="text-sm text-yellow-200"><strong>Pro tip:</strong> Stay toward the center — it gives you more room to dodge in either direction. Don't hug the edges.</p>
            </div>
          </div>

          <H id="lava-leaderboard" num="12" title="Lava Surfer — Leaderboard" icon={Trophy} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-300">Tap <strong className="text-yellow-400">LEADERBOARD</strong> on the home screen to see the top 10 all-time scores.</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-300">
              <li>Shows rank, player name, and score</li>
              <li>Top score so far: <strong className="text-yellow-400">5,385</strong></li>
              <li>Beat it and your name goes to #1</li>
              <li>You enter your name after each game if you qualify for the top 10</li>
            </ul>
            <div className="p-3 bg-gray-800 border border-gray-700 rounded-xl">
              <p className="text-sm text-gray-400">Sound can be toggled on/off with the speaker icon in the top-left corner.</p>
            </div>
          </div>

          <footer className="mt-12 pt-6 border-t border-gray-800 text-center space-y-3 pb-8">
            <div className="flex justify-center gap-4">
              <a href="https://rrdcarcade.com" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-green-400 hover:underline">Play RRDC Sports</a>
              <a href="https://lava-surfer.com" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-orange-400 hover:underline">Play Lava Surfer</a>
            </div>
            <p className="text-xs text-gray-600">Rolling River Day Camp — Game Guides</p>
          </footer>
        </main>
      </div>

      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="fixed bottom-6 right-6 bg-violet-700 text-white p-3 rounded-full shadow-lg hover:bg-violet-600 z-50 print:hidden" data-testid="back-to-top"><ChevronUp className="w-5 h-5" /></button>
      )}
    </div>
  );
}
